import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Text from '@amzn/meridian/text';
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Navigate, useNavigate, useLocation, matchPath } from 'react-router';
import { Link, Route, Routes } from 'react-router-dom';
import DataSource from '../api';
import accessBlockedImage from '../Assets/accessBlocked.svg';
import logo from '../Assets/InspireLogoSquidInk.png';
import Auth from '../Auth';
import { getCscEntityPrefix } from '../config/Config';
import { StateContext } from '../GlobalStateStore';
import { EmptyProfile, Profile } from '../Types';
import DataState from '../Types/DataState';
import { getCurrentWeek } from '../Util';
import { checkGoUser } from '../Util/User';
import { SettingPanel } from './Setting/SettingPanel';
import { NotificationsPopover } from './Notifications/NotificationsPopover';
import { Feature, isFeatureEnabled } from '../config/FeaturesConfig';
import { renderUrl, urls } from '../config/Url';
import CoachingFormWrapper from './CoachingForm/CoachingForm';
import ContactTable from './CoachingForm/ContactReview/ContactTableForm';
import CoachingHomeTm from './CoachingNotes/CoachingHomeTm';
import Dashboard from './Dashboard/Dashboard';
import DashboardDetails from './Dashboard/DashboardComponents/HmdDetails/DashboardDetails';
import Impersonate from './Dashboard/DashboardComponents/Impersonate';
import TeamProxyNotification from './Dashboard/DashboardComponents/TeamProxyNotification';
import styles from './PerformancePortalLanding.module.css';
import LighthouseProxyTeam from './ProxyTeam/Table/LighthouseMetrics/LighthouseProxyTeam';
import LighthouseTeam from './Team/Table/LighthouseMetrics/LighthouseTeam';
import FormLibraryHome from './FormLibrary/FormLibraryHome';
import CCXDetailsHome from './CCXDetails/CCXDetailsHome';
import PerformancePortalContext from './PerformancePortalContext';
import { UserContext, IUserContext } from './UserContext';
import TimeTrendsAndMetricsTeam from './Team/Table/TimeTrends/TimeTrendsAndMetricsTeam';
import ARCTeam from './Team/AssociateRichCards/Team';
import ResourceHub from './ResourceHub/ResourceHub';
import _ from 'lodash';
import OutlierManagementOMHome from './OutlierManagement/OutlierManagementOMHome';
import OutlierManagementTMHome from './OutlierManagement/OutlierManagementTMHome';
import OmCoaching from './OMCoaching/OmCoaching';
import TrainingTmHome from './Training/TrainingTmHome';
import TrainingAssignment from './Training/Assignment/TrainingAssignment';
import TrainingCsaHome from './Training/TrainingCsaHome';
import CsaPastContactsHome from './ContactListening/PastContactsHome';
import CoachAiButton from '../commons/CoachAiButton';

interface PerformancePortalIconProps {
  image: string;
  textID: string;
  textDefaultMessage: string;
  textDescription: string;
}

// Icon element with top part as image and bottom part as description.
const PerformancePortalIcon: React.FC<PerformancePortalIconProps> = ({
  image,
  textID,
  textDefaultMessage,
  textDescription,
}: PerformancePortalIconProps): React.ReactElement => {
  return (
    <div className={styles.iconItem}>
      <Column
        alignmentHorizontal='center'
        alignmentVertical='center'
        height='100%'
        spacing='none'
      >
        <img src={image} alt='Access blocked' />
        <FormattedMessage
          id={textID}
          defaultMessage={textDefaultMessage}
          description={textDescription}
        />
      </Column>
    </div>
  );
};

const NotFound: React.FC = (): React.ReactElement => {
  const redirectTimeout = 10;
  setTimeout(() => {
    window.location.assign(renderUrl(urls.dashboard));
  }, redirectTimeout);
  return <Text type='h200'>PAGE NOT FOUND!</Text>;
};

interface PerformancePortalLandingProps {
  inspireOptionsError: boolean;
}

const PerformancePortalLanding: React.FC<PerformancePortalLandingProps> = ({
  inspireOptionsError,
}): React.ReactElement => {
  const navigate = useNavigate();

  const { pathname, search, hash } = window.location;
  const [performancePortalContextValue, setPerformancePortalContextValue] =
    useState({});
  const [currentTab, setCurrentTab] = useState(`${pathname}${search}${hash}`);

  // bandaid solution to fix stale PerformancePortalContex in form library
  // TODO: pull out PerformancePortalContex from Form Library
  const clearPerformancePortalContextValue = () => {
    if (!_.isEmpty(performancePortalContextValue)) {
      setPerformancePortalContextValue({});
    }
  };

  const navigateTo = (href: string): void => {
    // before SPA, PerformancePortalContext would be implicitly cleared here as the page would have reloaded. this is no longer the case
    // legacy code was relying on PerformancePortalContext to be cleared, and so we had bugs appear i.e. https://t.corp.amazon.com/D222703355
    // this brings us back inline with clearing on tab change. TODO: we should remove our reliance on PerformancePortalContext in FormLibrary
    clearPerformancePortalContextValue();
    navigate(href);
    setCurrentTab(href);
  };

  const isProfileRequired = (pathname: string): boolean => {
    return (
      pathname === urls.defaultHome ||
      pathname.startsWith(urls.dashboard) ||
      pathname.startsWith(urls.realTimeHmdDetails) ||
      pathname.startsWith(urls.ccxDetails) ||
      pathname.startsWith(urls.hmdDetails)
    );
  };

  const [dashboardTabVisible, setDashboardTabVisible] = useState(true);
  const [teamTabVisible, setTeamTabVisible] = useState(false);
  const [metricsTabVisible, setMetricsTabVisible] = useState(false);
  const [proxyTabVisible, setProxyTabVisible] = useState(false);
  const [impersonateVisible, setImpersonateVisible] = useState(false);
  const globalState = useContext(StateContext);
  const apiNaws = DataSource.getDataFeed('NAWS');

  const [inspireAccessBlocked, setInspireAccessBlocked] = useState(
    isFeatureEnabled(Feature.INSPIRE_ACCESS_BLOCKED)
  );
  const isNotificationCenterEnabled = isFeatureEnabled(
    Feature.NOTIFICATION_CENTER
  );
  const coachAiEnabled = isFeatureEnabled(Feature.COACH_AI);

  const isOmCoachingPageEnabled = isFeatureEnabled(Feature.OM_COACHING_PAGE);
  const isOmMetricsPageEnabled = isFeatureEnabled(Feature.OM_METRICS_PAGE);

  const isResourceHubEnabled = isFeatureEnabled(Feature.RESOURCE_HUB);

  const localSessionid = window.localStorage.getItem(
    'PerformancePortalService_sessionID'
  );

  const { pathname: routerPath } = useLocation();

  // this pattern match is used to get userId from the current route param
  const patterns = [
    `${urls.dashboard}/:userId`,
    `${urls.hmdDetails}/:userId`,
    `${urls.realTimeHmdDetails}/:userId`,
    `${urls.coachingNotes}/:userId`,
  ];

  const match = patterns
    .map(pattern => matchPath(pattern, routerPath))
    .find(match => match !== null);

  const routeUserID = match?.params?.userId;

  const [profile, setProfile] = useState<Profile>(EmptyProfile);
  const [profileState, setProfileState] = useState(DataState.INITIALIZED);
  const [routeProfile, setRouteProfile] = useState<Profile>(EmptyProfile);
  const [routeProfileState, setRouteProfileState] = useState(
    DataState.INITIALIZED
  );

  const displayedProfile = routeUserID ? routeProfile : profile;
  const displayedProfileState = routeUserID ? routeProfileState : profileState;

  const { inspireOptions } = globalState.state;
  const {
    userID,
    cscEntityPrefix,
    isTm,
    isOm,
    isAdmin,
    requesterUserID,
    requesterOwner,
    requesterUsername,
    isExclusivelyTm,
    isExclusivelyOm,
  } = inspireOptions.data;

  const [teamProxyEnabled, setTeamProxyEnabled] = useState(
    isFeatureEnabled(Feature.TEAM_PROXY)
  );

  const [associateRichCardsEnabled] = useState(
    isFeatureEnabled(Feature.ASSOCIATE_RICH_CARDS)
  );

  const [isOutlierFeatureEnabled] = useState(
    isFeatureEnabled(Feature.OUTLIER_MANAGEMENT)
  );

  const [outlierTabVisible, setOutlierTabVisible] = useState(
    isOutlierFeatureEnabled
  );

  const [isTrainingFeatureEnabled] = useState(
    isFeatureEnabled(Feature.TRAINING)
  );

  const [trainingTabVisible, setTrainingTabVisible] = useState(
    isTrainingFeatureEnabled
  );

  const [isCsaPastContactsFeatureEnabled] = useState(
    isFeatureEnabled(Feature.CSA_CONTACT_LISTENING)
  );

  const [isPastContactsTabVisible, setPastContactsTabVisible] = useState(
    isCsaPastContactsFeatureEnabled
  );

  const coachingTabVisible = isOm && isOmCoachingPageEnabled;

  useEffect(() => {
    async function onLoad(userId: string): Promise<void> {
      setInspireAccessBlocked(isFeatureEnabled(Feature.INSPIRE_ACCESS_BLOCKED));
      setTeamProxyEnabled(isFeatureEnabled(Feature.TEAM_PROXY));
    }

    const { userID: userID_ } = globalState.state.inspireOptions.data;
    if (userID_) {
      onLoad(userID_);
    }
  }, [globalState.state.inspireOptions.data]);

  useEffect(() => {
    fetchDisplayProfile();
  }, [apiNaws, cscEntityPrefix, inspireOptions.state, routeUserID, userID]);

  useEffect(() => {
    fetchProfile();
  }, [inspireOptions.state, teamProxyEnabled]);

  const fetchProfileAndDisplayProfile = () => {
    fetchDisplayProfile();
    fetchProfile();
  };

  const fetchDisplayProfile = () => {
    if (inspireOptions.state !== DataState.LOADED) {
      return;
    }
    if (!routeUserID) {
      // there's no userID parameter in the URL
      return;
    }
    // fetch the profile in the URL to populate dashboard/hmd

    const variables = {
      userID: `${cscEntityPrefix}:${routeUserID}`,
    };

    apiNaws.getProfileData(
      variables,
      (payload: Profile) => {
        setRouteProfile(payload);
        if (userID === routeUserID) setProfile(payload);
      },
      (payload: string) => {
        setRouteProfileState(payload);
        if (userID === routeUserID) setProfileState(payload);
      }
    );
  };

  const fetchProfile = () => {
    if (inspireOptions.state !== DataState.LOADED && !userID) {
      return;
    }
    if (
      (isProfileRequired(pathname) && !routeUserID) ||
      (teamProxyEnabled && isTm && userID !== routeUserID)
    ) {
      // (1) The user is visiting dashboard/hmd pages,
      //   and there's no user specified in the URL,
      //   so the profile hasn't been fetched before.
      // (2) The user is a TM, URL user is different even if it exists,
      //   and we need profile for TMProxy.
      const variables = {
        userID: `${cscEntityPrefix}:${userID}`,
      };
      apiNaws.getProfileData(variables, setProfile, setProfileState);
    }
  };

  useEffect(() => {
    // Hide team tab button, and redirect non-eligible users appropriately
    if (userID) {
      setTeamTabVisible(isTm);

      if (pathname === urls.team) {
        if (!isTm) {
          navigate(urls.dashboard);
        }
      }
    }
  }, [inspireOptions.data, pathname]);

  useEffect(() => {
    // Hide proxy-tab button, and redirect non-eligable users to dashboard
    if (userID && profileState === DataState.LOADED) {
      // Team tab visibility logic
      const isTeamTabVisible =
        isTm &&
        (profile.directReports.length > 0 ||
          profile.sharedProfileCoachees.length > 0);
      setTeamTabVisible(isTeamTabVisible);

      // Set proxy tab visible if
      // (1) Feature is enabled via appConfig
      // (2) User has proxy coachees
      // (3) User is an OM OR User is a GO user TM
      // Note: TM-only users should NOT see the proxy tab
      const tabVisible =
        teamProxyEnabled &&
        profile.proxyCoachees.length > 0 &&
        (isOm || checkGoUser(profile.user?.owner));
      setProxyTabVisible(tabVisible);

      const isTrendAndMetricsVisible = isFeatureEnabled(Feature.TREND_METRICS);

      const isMetricsTabVisible =
        (isExclusivelyTm &&
          profile.directReports.length > 0 &&
          isTrendAndMetricsVisible) ||
        (isOm && isOmMetricsPageEnabled && isTrendAndMetricsVisible);
      setMetricsTabVisible(isMetricsTabVisible);

      const isTrainingTabVisible = !isExclusivelyOm && isTrainingFeatureEnabled;
      setTrainingTabVisible(isTrainingTabVisible);

      const isPastContactsTabVisible =
        !isTm &&
        !isOm &&
        profile.directReports.length == 0 &&
        isCsaPastContactsFeatureEnabled;

      setPastContactsTabVisible(isPastContactsTabVisible);

      const isOutlierTabVisible =
        isOutlierFeatureEnabled &&
        (isTm || isOm) &&
        profile.directReports.length > 0;

      setOutlierTabVisible(isOutlierTabVisible);

      // Determine where to redirect based on user type and tab visibility
      const shouldRedirect =
        (!tabVisible && pathname === urls.proxy) ||
        (!isMetricsTabVisible && pathname === urls.metrics) ||
        (!isTeamTabVisible && pathname === urls.team) ||
        (!isOutlierTabVisible && pathname === urls.outlierManagement) ||
        (!isTrainingTabVisible && pathname === urls.training) ||
        (!isPastContactsTabVisible && pathname === urls.pastContacts);

      if (shouldRedirect) {
        if (coachingTabVisible) {
          navigate(urls.coaching);
        } else {
          navigate(urls.dashboard);
        }
      }
    }
  }, [inspireOptions.data, pathname, profileState]);

  useEffect(() => {
    if (Auth.isImpersonating() || isAdmin) {
      setImpersonateVisible(true);
    }
  }, [inspireOptions.data]);

  useEffect(() => {
    if (
      !requesterUserID ||
      !requesterOwner ||
      !requesterUsername ||
      !localSessionid
    )
      return;
    const pathSegment = pathname.split('/');
    const spa = window.csa('SPA');

    window.csa('Events')('setEntity', {
      session: {
        id: localSessionid,
      },
      user: {
        id: requesterUserID,
        cscEntityPrefix: getCscEntityPrefix(),
        isTM: `${isTm}`, // Nexus Schema requires a String
        requestOwner: requesterOwner,
        username: requesterUsername,
      },
      page: {
        pageType: 'Inspire',
        subPageType: pathSegment[1] ? pathSegment[1] : 'dashboard',
      },
    });
    spa(currentTab);
  }, [localSessionid, inspireOptions.data]);

  // We don't have to make this Box as Responsive component
  // since it is set to Responsive in the child node
  // And this Responsive would format the view which leads improper view for
  // Show More functionality of Lighthouse Metrics

  if (inspireAccessBlocked) {
    return (
      <PerformancePortalIcon
        image={accessBlockedImage}
        textID='cs_perfportal_inspire_access_blocked'
        textDefaultMessage='Sorry! You don’t have access yet.'
        textDescription='Inspire Access Blocked'
      />
    );
  }

  function getCurrentTab(currentTab: string) {
    const parts = currentTab.split('/');
    if (parts.length > 2) {
      return '/' + parts.slice(1, 2).join('/');
    }
    return currentTab;
  }

  const profileLoaded = profileState === DataState.LOADED;
  const hasDirectReports = profileLoaded && profile?.directReports.length > 0; // using ? just as an additional guardrail even though we ensure profile is loaded beforehand

  const isExclusivelyTMWithDirectReports =
    profileLoaded && isExclusivelyTm && hasDirectReports;
  const isOMWithDirectReports =
    profileLoaded && isTm && isOm && hasDirectReports;

  const userContextValue: IUserContext = {
    loggedInProfile: profile,
    loggedInProfileState: profileState,
    displayedProfile: displayedProfile,
    displayedProfileState: displayedProfileState,
    refetchProfiles: fetchProfileAndDisplayProfile,
  };

  return (
    <UserContext.Provider value={userContextValue}>
      <PerformancePortalContext.Provider
        value={{
          performancePortalContextValue,
          setPerformancePortalContextValue,
          clearPerformancePortalContextValue,
          refetchProfiles: fetchProfileAndDisplayProfile,
        }}
      >
        <Row spacing='none' spacingInset='none' alignmentHorizontal='center'>
          <Box width='1440px' maxWidth='1440px'>
            {!routerPath.startsWith(`${urls.coachingForm}/`) &&
            !routerPath.startsWith(`${urls.contactReview}/`) ? (
              <Row spacingInset='300 500 400' spacing='none'>
                <Link to={renderUrl(urls.dashboard)}>
                  <img
                    src={logo}
                    alt='Inspire'
                    className={styles.inspireLogo}
                  />
                </Link>
                <Row>
                  <TabGroup
                    value={getCurrentTab(routerPath)}
                    onChange={navigateTo}
                  >
                    {dashboardTabVisible && (
                      <Tab value={renderUrl(urls.dashboard)}>
                        <FormattedMessage
                          id='cs_perfportal_dashboard'
                          defaultMessage='Dashboard'
                          description='Dashboard button'
                        />
                      </Tab>
                    )}
                    {isPastContactsTabVisible && (
                      <Tab value={renderUrl(urls.pastContacts)}>
                        <FormattedMessage
                          id='cs_perfportal_past_contacts_pascal'
                          defaultMessage='Past Contacts'
                          description='Past contacts button'
                        />
                      </Tab>
                    )}
                    {teamTabVisible && (
                      <Tab value={renderUrl(urls.team)}>
                        <FormattedMessage
                          id='cs_perfportal_team'
                          defaultMessage='Team'
                        />
                      </Tab>
                    )}
                    {coachingTabVisible && (
                      <Tab value={renderUrl(urls.coaching)}>
                        <FormattedMessage
                          id='cs_perfportal_coaching'
                          defaultMessage='Coaching'
                        />
                      </Tab>
                    )}
                    {!isOm && metricsTabVisible && (
                      <Tab value={renderUrl(urls.metrics)}>
                        <FormattedMessage
                          id='cs_perfportal_metrics'
                          defaultMessage='Metrics'
                        />
                      </Tab>
                    )}
                    {trainingTabVisible && (
                      <Tab
                        value={renderUrl(urls.training)}
                        data-csa-c-type='link'
                        data-csa-c-slot-id='open-training-tab-slot'
                        data-csa-c-content-id='open-training-tab'
                        data-csa-c-description='Navigate to training'
                        data-csa-c-tag1='Training'
                      >
                        <FormattedMessage
                          id='cs_perfportal_training'
                          defaultMessage='Training'
                          description='Training button'
                        />
                      </Tab>
                    )}
                    {outlierTabVisible && (
                      <Tab
                        value={renderUrl(urls.outlierManagement)}
                        data-csa-c-type='link'
                        data-csa-c-slot-id='open-outlier-management-tab-slot'
                        data-csa-c-content-id='open-outlier-management-tab'
                        data-csa-c-description='Navigate to outlier management'
                        data-csa-c-tag1='Outlier Management'
                      >
                        <FormattedMessage
                          id='cs_perfportal_outliers'
                          defaultMessage='Outliers'
                          description='Outlier button'
                        />
                      </Tab>
                    )}
                    {isOm && metricsTabVisible && (
                      <Tab value={renderUrl(urls.metrics)}>
                        <FormattedMessage
                          id='cs_perfportal_metrics'
                          defaultMessage='Metrics'
                        />
                      </Tab>
                    )}
                    {isResourceHubEnabled &&
                      // profile state check to prevent resource hub tab from loading then changing position as Metrics tab is rendered
                      (profileState === DataState.LOADED ||
                        profileState === DataState.ERROR) && (
                        <Tab value={renderUrl(urls.resourceHub)}>
                          <FormattedMessage
                            id='cs_perfportal_resource_center'
                            defaultMessage='Resource Center'
                          />
                        </Tab>
                      )}

                    {proxyTabVisible && (
                      <Tab
                        value={renderUrl(urls.proxy)}
                        data-csa-c-type='link'
                        data-csa-c-slot-id='open-proxy-team-tab-slot'
                        data-csa-c-content-id='open-proxy-team-tab'
                        data-csa-c-description='Navigate to proxy tab'
                        data-csa-c-tag1='Proxy_Team'
                      >
                        <FormattedMessage
                          id='cs_perfportal_proxy'
                          defaultMessage='Proxy'
                          description='Proxy Team button'
                        />
                      </Tab>
                    )}
                  </TabGroup>
                </Row>
                {proxyTabVisible && (
                  <TeamProxyNotification navigateTo={navigateTo} />
                )}
                {impersonateVisible && (
                  <div className={styles.impersonateStyle}>
                    <Impersonate
                      profile={displayedProfile}
                      profileState={displayedProfileState}
                    />
                  </div>
                )}
                <Row width='100%' widths={['fill', 'fit']}>
                  {coachAiEnabled && isTm && (
                    <>
                      <CoachAiButton />
                    </>
                  )}
                  {isNotificationCenterEnabled && (
                    <>
                      <NotificationsPopover />
                    </>
                  )}
                </Row>
                <Row
                  width={isNotificationCenterEnabled ? '' : '100%'}
                  widths={['fill', 'fit']}
                >
                  <SettingPanel />
                </Row>
              </Row>
            ) : (
              <Row />
            )}

            <Box spacingInset='400 500' maxWidth='1440px'>
              <Row
                width='100%'
                widths='fill'
                alignmentHorizontal='center'
                spacing='none'
              >
                {inspireOptionsError && (
                  <Alert type='error'>
                    <FormattedMessage
                      id='cs_perfportal_error_refresh_page'
                      defaultMessage='Something went wrong. Please try refreshing the page. 
                    If the problem persists, please submit a SIM ticket for us to troubleshoot.'
                    />
                  </Alert>
                )}

                {!inspireOptionsError && (
                  <Routes>
                    <Route
                      path='/'
                      element={
                        <Dashboard
                          loggedInProfile={profile}
                          displayedProfile={displayedProfile}
                          loggedInProfileState={profileState}
                          displayedProfileState={displayedProfileState}
                        />
                      }
                    />
                    <Route
                      path={`${urls.dashboard}/:userId?`}
                      element={
                        <Dashboard
                          loggedInProfile={profile}
                          displayedProfile={displayedProfile}
                          loggedInProfileState={profileState}
                          displayedProfileState={displayedProfileState}
                        />
                      }
                    />
                    <Route
                      path={`${urls.hmdDetails}/:userId?`}
                      element={
                        <DashboardDetails
                          propProfile={displayedProfile}
                          propProfileState={displayedProfileState}
                          isHMDRealTime={false}
                        />
                      }
                    />
                    <Route
                      path={`${urls.realTimeHmdDetails}/:userId?`}
                      element={
                        <DashboardDetails
                          propProfile={displayedProfile}
                          propProfileState={displayedProfileState}
                          isHMDRealTime={true}
                        />
                      }
                    />
                    <Route
                      path={`${urls.team}/:userId?`}
                      element={
                        associateRichCardsEnabled ? (
                          <ARCTeam />
                        ) : (
                          <LighthouseTeam />
                        )
                      }
                    />
                    <Route
                      path={`${urls.proxy}/:userId?`}
                      element={<LighthouseProxyTeam />}
                    />
                    <Route
                      path={`${urls.coachingNotes}/:userId?`}
                      element={
                        <CoachingHomeTm
                          coachProfile={profile}
                          coachProfileState={profileState}
                          coacheeProfile={displayedProfile}
                          coacheeProfileState={displayedProfileState}
                        />
                      }
                    />
                    {coachingTabVisible && (
                      <Route
                        path={`${urls.coaching}/:userId?`}
                        element={
                          <OmCoaching
                            loggedInProfile={profile}
                            displayedProfile={displayedProfile}
                            loggedInProfileState={profileState}
                            displayedProfileState={displayedProfileState}
                          />
                        }
                      />
                    )}
                    <Route
                      path={`${urls.coachingForm}/:userId?`}
                      element={<CoachingFormWrapper />}
                    />
                    <Route
                      path={`${urls.contactReview}/:userId/:contactId?`}
                      element={<FormLibraryHome />}
                    />
                    <Route
                      path={`${urls.contactTable}/:userId?`}
                      element={<ContactTable />}
                    />
                    <Route
                      path={`${urls.ccxDetails}/:userId?`}
                      element={<CCXDetailsHome />}
                    />
                    {outlierTabVisible && (
                      <Route
                        path={`${urls.outlierManagement}/:userId?`}
                        Component={() => {
                          if (!profileLoaded) return null;
                          if (isExclusivelyTMWithDirectReports) {
                            return (
                              <OutlierManagementTMHome
                                profile={displayedProfile}
                                profileState={displayedProfileState}
                              />
                            );
                          }
                          if (isOMWithDirectReports) {
                            return (
                              <OutlierManagementOMHome
                                profile={displayedProfile}
                                profileState={displayedProfileState}
                              />
                            );
                          }

                          // If the code reaches here the user has neither TM, nor OM nor has direct report.
                          // so we reroute to dashboard as the user is effectively CSA.
                          return <Navigate to={urls.dashboard} replace />;
                        }}
                      />
                    )}

                    {trainingTabVisible && (
                      <Route
                        path={`${urls.training}/:userId?`}
                        Component={() => {
                          if (!profileLoaded) return null;
                          if (!isTm)
                            return (
                              <TrainingCsaHome
                                profile={displayedProfile}
                                profileState={displayedProfileState}
                              />
                            );
                          return (
                            <TrainingTmHome
                              profile={displayedProfile}
                              profileState={displayedProfileState}
                            />
                          );
                        }}
                      />
                    )}

                    {isPastContactsTabVisible && (
                      <Route
                        path={`${urls.pastContacts}/:userId?`}
                        Component={() => {
                          if (!profileLoaded) return null;
                          return (
                            <CsaPastContactsHome
                              profile={displayedProfile}
                              profileState={displayedProfileState}
                            />
                          );
                        }}
                      />
                    )}

                    <Route
                      path={`${urls.training}/assignment/:userId?`}
                      Component={() => {
                        if (!profileLoaded || !trainingTabVisible) return null;

                        return (
                          <TrainingAssignment profile={displayedProfile} />
                        );
                      }}
                    />

                    <Route
                      path={`${urls.metrics}/:userId?`}
                      element={
                        profileState !== DataState.LOADED ? null : profile
                            .directReports.length === 0 ? (
                          <Navigate to={urls.dashboard} replace />
                        ) : (
                          <TimeTrendsAndMetricsTeam
                            profile={displayedProfile}
                            profileState={displayedProfileState}
                          />
                        )
                      }
                    />
                    {isResourceHubEnabled && (
                      <Route
                        path={`${urls.resourceHub}/:userId?`}
                        element={<ResourceHub />}
                      />
                    )}
                    <Route path='*' element={<NotFound />} />
                  </Routes>
                )}
              </Row>
            </Box>
          </Box>
        </Row>
      </PerformancePortalContext.Provider>
    </UserContext.Provider>
  );
};

export default PerformancePortalLanding;
